import { forwardRef } from "react";
import { F } from "@/components/sheets/SheetFrame";
import { Table } from "@/components/sheets/SheetTable";
import { typologyLabel } from "@/lib/customTypologies";
import { useLogo, LOGO_LIGHT_SRC, LOGO_RATIO } from "@/lib/brand";
import { approvalText } from "@/lib/feedback";

export const COVER_PER_PAGE = 12;
const X = 150, TW = 254, GAP = 4, TILE_W = (TW - 3 * GAP) / 4, IMG_H = 40, TILE_H = 52;

const wrap = (s, n, max) => {
  const out = [];
  let cur = "";
  String(s || "").split(/\s+/).filter(Boolean).forEach((w) => {
    if (cur && `${cur} ${w}`.length > n) { out.push(cur); cur = w; } else cur = cur ? `${cur} ${w}` : w;
  });
  if (cur) out.push(cur);
  return out.slice(0, max);
};

const Info = ({ y, label, value }) => (
  <g>
    <text x={20} y={y} fontSize={2.2} fill="#94A3B8" fontFamily={F}>{label.toUpperCase()}</text>
    <text x={20} y={y + 5} fontSize={3.4} fill="#F1F5F9" fontFamily={F}>{String(value || "—").slice(0, 46)}</text>
  </g>
);

const BrandPanel = ({ tb, page, sum }) => {
  const logo = useLogo(LOGO_LIGHT_SRC), name = wrap(tb.project_name || "Untitled project", 20, 3);
  const info = [["Client", tb.client], ["Site / address", tb.site], ["Architect / consultant", tb.architect], ["Fabricator / contractor", tb.contractor || tb.company],
    ["Date · revision", `${tb.date || "—"}  ·  Rev ${tb.revision || "—"}`], ["Drawing set", tb.drawing_no], ["Schedule", `${sum.n} items · ${sum.units} units · ${sum.area} m²`]];
  return (
    <g data-testid="cover-brand-panel">
      <rect x={10} y={10} width={128} height={277} fill="#0F172A" />
      {tb.hide_logo
        ? <text x={20} y={36} fontSize={6} fill="#fff" fontFamily={F} fontWeight="bold">{(tb.company || "ALUMINIUM SYSTEMS").slice(0, 24)}</text>
        : <image href={logo} x={18} y={22} width={110} height={110 / LOGO_RATIO} preserveAspectRatio="xMinYMid meet" data-testid="cover-logo" />}
      <rect x={20} y={58} width={24} height={1.2} fill="#22D3EE" />
      <text x={20} y={68} fontSize={3} fill="#67E8F9" fontFamily={F} fontWeight="bold">CLIENT PRESENTATION</text>
      <text x={20} y={73.5} fontSize={2.5} fill="#94A3B8" fontFamily={F}>ALUMINIUM WINDOWS &amp; DOORS</text>
      {name.map((l, i) => <text key={i} x={20} y={88 + i * 10} fontSize={8} fill="#fff" fontFamily={F} fontWeight="bold">{l}</text>)}
      {info.map(([l, v], i) => <Info key={l} y={128 + i * 15} label={l} value={v} />)}
      <line x1={20} y1={244} x2={128} y2={244} stroke="#334155" strokeWidth={0.3} />
      <text x={20} y={252} fontSize={2.2} fill="#94A3B8" fontFamily={F}>PREPARED BY</text>
      <text x={20} y={258} fontSize={3.4} fill="#F1F5F9" fontFamily={F} fontWeight="bold">{(tb.company || (tb.hide_logo ? "—" : "ARTISAN GUILD INFRA")).slice(0, 34)}</text>
      <text x={20} y={264} fontSize={2.6} fill="#CBD5E1" fontFamily={F}>{`Drawn ${tb.drawn_by || "—"}  ·  Checked ${tb.checked_by || "—"}`.slice(0, 50)}</text>
      <text x={20} y={279} fontSize={2.2} fill="#64748B" fontFamily={F}>{`COVER ${page.index + 1} OF ${page.pages}`}</text>
    </g>
  );
};

const Tile = ({ it, x, y, snap }) => (
  <g data-testid={`cover-tile-${it.tag}`}>
    <rect x={x} y={y} width={TILE_W} height={IMG_H} fill="#EEF2F6" />
    {snap
      ? <image href={snap} x={x} y={y} width={TILE_W} height={IMG_H} preserveAspectRatio="xMidYMid meet" />
      : <text x={x + TILE_W / 2} y={y + IMG_H / 2} fontSize={2.6} fill="#94A3B8" fontFamily={F} textAnchor="middle">Preparing 3D…</text>}
    <rect x={x + 1.5} y={y + 1.5} width={Math.max(10, it.tag.length * 2.4 + 4)} height={5.2} rx={1} fill="#0E7490" />
    <text x={x + 3.5} y={y + 5.3} fontSize={3} fill="#fff" fontFamily={F} fontWeight="bold">{it.tag}</text>
    <text x={x} y={y + IMG_H + 4.8} fontSize={3} fill="#0F172A" fontFamily={F} fontWeight="bold">{`${it.width} × ${it.height}  ·  Qty ${it.quantity || 1}`}</text>
    <text x={x} y={y + IMG_H + 9} fontSize={2.2} fill="#64748B" fontFamily={F}>{typologyLabel(it.typology).slice(0, 44)}</text>
  </g>
);

const COLS = [{ h: "TAG", w: 14 }, { h: "ITEM / LOCATION", w: 42 }, { h: "TYPOLOGY", w: 42 }, { h: "SIZE W × H (mm)", w: 28 }, { h: "QTY", w: 10 }, { h: "SYSTEM", w: 36 }, { h: "GLASS", w: 32 }, { h: "CLIENT APPROVAL", w: 50 }];
const row = (it, ap) => [it.tag, `${it.name || it.type}${it.location ? ` · ${it.location}` : ""}`, typologyLabel(it.typology), `${it.width} × ${it.height}`, it.quantity || 1, it.profile_system, it.glass_spec, approvalText(ap?.[it.id])];

export const CoverSheet = forwardRef(({ tb, sheetNo, total, page, snaps = {} }, ref) => {
  const all = page.all, sum = { n: all.length, units: all.reduce((a, i) => a + (i.quantity || 1), 0), area: all.reduce((a, i) => a + (i.width * i.height * (i.quantity || 1)) / 1e6, 0).toFixed(2) };
  const ty = 36, gy = ty + 5 * (page.items.length + 1) + 14;
  return (
    <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 297" width="420mm" height="297mm" data-sheet={`COVER-${page.index + 1}`} data-testid="cover-sheet">
      <rect x={0} y={0} width={420} height={297} fill="#fff" />
      <rect x={10} y={10} width={400} height={277} fill="none" stroke="#0F172A" strokeWidth={0.6} />
      <BrandPanel tb={tb} page={page} sum={sum} />
      <text x={X} y={22} fontSize={5} fill="#0F172A" fontFamily={F} fontWeight="bold">{`WINDOW & DOOR SCHEDULE${page.index ? " (CONTD.)" : ""}`}</text>
      <text x={X} y={28} fontSize={2.6} fill="#555" fontFamily={F}>{`${sum.n} items · ${sum.units} units · ${sum.area} m² total opening area · sizes in mm`}</text>
      <text x={404} y={22} fontSize={2.6} fill="#555" fontFamily={F} textAnchor="end">{`SHEET ${sheetNo} OF ${total}`}</text>
      <Table x={X} y={ty} rh={5} cols={COLS} rows={page.items.map((it) => row(it, page.approvals))} title="" testid="cover-schedule" />
      <text x={X} y={gy - 3} fontSize={3.4} fill="#0F172A" fontFamily={F} fontWeight="bold">3D OVERVIEW</text>
      {page.items.map((it, i) => <Tile key={it.id} it={it} x={X + (i % 4) * (TILE_W + GAP)} y={gy + Math.floor(i / 4) * (TILE_H + GAP)} snap={snaps[`c_${it.id}`]} />)}
      <text x={X} y={283} fontSize={2.1} fill="#64748B" fontFamily={F}>3D views are illustrative, viewed from outside and not to scale. Refer to the elevation and section sheets for fabrication dimensions.</text>
    </svg>
  );
});
CoverSheet.displayName = "CoverSheet";
